import { useCallback, useContext, useEffect, useState } from "react";

import MyLocationIcon from "@mui/icons-material/MyLocation";
import { Box, CircularProgress, debounce, IconButton } from "@mui/material";
import { colors, toast, useMapColorScheme } from "@rubi/ui";
import {
  AdvancedMarker,
  Map,
  MapControl,
  Marker,
  Pin,
  useMap,
} from "@vis.gl/react-google-maps";
import { useSearchParams } from "react-router-dom";

import { AuthStateContext } from "@/contexts/AuthContext";
import { useServiceConfigurations } from "@/hooks/useCommunities";
import { BulletinResourceResponse } from "@/types";

const containerStyle = {
  width: "calc(100%)",
  height: "calc(100vh)",
  // marginLeft: "8px",
  // marginRight: "8px",
};

function AlertsMap({
  alerts,
  alertClickHandler,
  selectedAlertId,
  isLoading,
}: {
  alerts: BulletinResourceResponse[];
  alertClickHandler: (alert: BulletinResourceResponse, index: number) => void;
  selectedAlertId: number | null;
  isLoading: boolean;
}) {
  const mapColorScheme = useMapColorScheme();
  const googleMapId = "4380cd85df35e961";
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [usersLocation, setUsersLocation] =
    useState<google.maps.LatLngLiteral | null>(null);
  const { currentUser } = useContext(AuthStateContext);
  const serviceConfigurations = useServiceConfigurations();

  const selectedAlert = alerts.find((alert) => alert.id === selectedAlertId);
  const [searchParams, setSearchParams] = useSearchParams();
  const compareAlertId = searchParams.get("compareBulletinId");
  const zoomFactor = 13;

  const map = useMap();
  const [bounds, setBounds] = useState(map?.getBounds());

  const ne = bounds?.getNorthEast().toJSON();
  const sw = bounds?.getSouthWest().toJSON();

  const extendBounds = (
    bounds: google.maps.LatLngBounds,
    paddingFactor = 0.1,
  ) => {
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const latPadding = (ne.lat() - sw.lat()) * paddingFactor;
    const lngPadding = (ne.lng() - sw.lng()) * paddingFactor;

    return new google.maps.LatLngBounds(
      { lat: sw.lat() - latPadding, lng: sw.lng() - lngPadding },
      { lat: ne.lat() + latPadding, lng: ne.lng() + lngPadding },
    );
  };

  const setBoundsInSearchParams = () => {
    if (bounds) {
      const extendedBounds = extendBounds(bounds);

      const ne = extendedBounds.getNorthEast().toJSON();
      const sw = extendedBounds.getSouthWest().toJSON();

      setSearchParams((searchParams) => {
        searchParams.set("bounds", `${sw.lat},${sw.lng},${ne.lat},${ne.lng}`);
        return searchParams;
      });
    }
  };

  const debouncedSetBoundsInSearchParams = useCallback(
    debounce(setBoundsInSearchParams, 500),
    [ne, sw],
  );

  const setMapCenterToFirstValidAlert = () => {
    const validCoordinates = alerts
      .map(
        (alert) =>
          alert.location?.addresses?.length &&
          alert.location.addresses[0]?.geometry,
      )
      .filter(
        (geometry) =>
          geometry &&
          typeof geometry.lat === "number" &&
          typeof geometry.lng === "number",
      ) as google.maps.LatLngLiteral[];

    if (validCoordinates.length > 0 && isMapLoaded) {
      const bounds = new google.maps.LatLngBounds();
      validCoordinates.forEach((geometry) => {
        bounds.extend(geometry);
      });
      map?.fitBounds(bounds, {
        top: 100,
        right: 100,
        bottom: 100,
        left: 100,
      });
      const currentZoom = map?.getZoom();
      if (currentZoom !== undefined && currentZoom > zoomFactor) {
        map?.setZoom(zoomFactor);
      }
    }
  };

  const adjustMapBoundsToAlert = (alert: BulletinResourceResponse) => {
    const bounds = new google.maps.LatLngBounds();

    // get all alerts to show
    const alertsToShow = [alert];
    if (compareAlertId) {
      const compareAlert = alerts.find(
        (a) => a.id.toString() === compareAlertId,
      );
      if (compareAlert) {
        alertsToShow.push(compareAlert);
      }
    }

    // collect all valid coordinates
    alertsToShow.forEach((currentAlert) => {
      const validCoordinates = currentAlert.location?.addresses?.filter(
        (address) =>
          address.geometry &&
          typeof address.geometry.lat === "number" &&
          typeof address.geometry.lng === "number",
      );

      validCoordinates?.forEach((address) => bounds.extend(address.geometry));
    });

    // if there are multiple coordinates, adjust the map to show all points
    if (!bounds.isEmpty()) {
      // Check if all coordinates are already within current map bounds
      const currentBounds = map?.getBounds();
      let allPointsInView = true;

      if (currentBounds) {
        // Check if all points in the bounds are contained in the current map view
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        if (!currentBounds.contains(ne) || !currentBounds.contains(sw)) {
          allPointsInView = false;
        }
      } else {
        // If we can't get current bounds, default to adjusting
        allPointsInView = false;
      }

      // Only adjust map if some points are outside current view
      if (!allPointsInView) {
        map?.fitBounds(bounds, {
          top: 100,
          right: 100,
          bottom: 100,
          left: 100,
        });
        const currentZoom = map?.getZoom();
        if (currentZoom !== undefined && currentZoom > zoomFactor) {
          map?.setZoom(zoomFactor);
        }
      }
    } else {
      // if there are no valid coordinates, keep the current view
      return;
    }
  };

  useEffect(() => {
    // get bounds from search params, this is used to when user directly
    // access the map page (no bounds in search params), the map will be
    // centered to the user's location
    const bounds = searchParams.get("bounds");
    if (isMapLoaded && selectedAlertId === null && !bounds) {
      getCurrentPosition();
    }
  }, [isMapLoaded, selectedAlertId]);

  useEffect(() => {
    // this is called when the bounds are changed (user manually zoom in/out/move the map)
    if (isMapLoaded) {
      debouncedSetBoundsInSearchParams();
    }
    return () => debouncedSetBoundsInSearchParams.clear();
  }, [bounds, isMapLoaded]);

  useEffect(() => {
    // this is called when the selected alert is changed (user click on an alert)
    if (selectedAlertId === null) {
      setMapCenterToFirstValidAlert();
    } else {
      const selectedAlert = alerts.find(
        (alert) => alert.id === selectedAlertId,
      );
      if (selectedAlert && isMapLoaded) {
        adjustMapBoundsToAlert(selectedAlert);
      }
    }
  }, [selectedAlertId, isMapLoaded]);

  const getCurrentPosition = () => {
    // if browser refuse to share location, then show error.
    // otherwise, get the current position and set center of map to it.
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUsersLocation({ lat: latitude, lng: longitude });
        map?.setCenter({ lat: latitude, lng: longitude });
        if ((map?.getZoom() ?? 0) < zoomFactor) {
          map?.setZoom(zoomFactor);
        }
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          if (currentUser?.rubi_org_id) {
            const serviceConfig = Object.values(serviceConfigurations).find(
              (config) => {
                return (
                  config.id === currentUser.rubi_org_id ||
                  config.testID === currentUser.rubi_org_id
                );
              },
            );
            if (serviceConfig) {
              const { mapCenter } = serviceConfig;
              if (mapCenter) {
                map?.setCenter(mapCenter);
              }
              if ((map?.getZoom() ?? 0) < zoomFactor) {
                map?.setZoom(zoomFactor);
              }
            }
          }
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          toast.error("Location unavailable. Please try again.");
        } else if (error.code === error.TIMEOUT) {
          toast.error("Location request timed out.");
        } else {
          toast.error("An unknown error occurred while fetching location.");
        }
      },
    );
  };

  /**
   *  Function to get the user's current location and pan to it
   */
  const panToCurrentLocation = () => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "granted") {
          getCurrentPosition();
        } else if (result.state === "denied") {
          toast.error(
            "Please enable location services in your browser and refresh the page to use this feature.",
          );
        } else if (result.state === "prompt") {
          getCurrentPosition();
        }
      });
    } else if (navigator.geolocation) {
      getCurrentPosition();
    }
  };

  const handleMarkerClick = (alert, index) => {
    alertClickHandler(alert, index);
    adjustMapBoundsToAlert(alert);
  };

  const getMarkers = (alerts: BulletinResourceResponse[]) =>
    alerts.flatMap(
      (alert, index) =>
        alert.location?.addresses?.map((address, addressIndex) => {
          const { lat, lng } = address?.geometry || {};
          if (typeof lat === "number" && typeof lng === "number") {
            const glyphColor =
              selectedAlert?.id === alert?.id ||
              compareAlertId === alert?.id.toString()
                ? colors.primary.contrastText
                : "";
            return (
              <AdvancedMarker
                key={`${index}-${addressIndex}`}
                position={{ lat, lng }}
                onClick={() => {
                  handleMarkerClick(alert, index);
                }}
                clickable
                zIndex={
                  selectedAlert?.id === alert?.id
                    ? 1000
                    : compareAlertId === alert?.id.toString()
                      ? 900
                      : 0
                }
              >
                <Pin
                  scale={
                    selectedAlert?.id === alert?.id ||
                    compareAlertId === alert?.id.toString()
                      ? 1.5
                      : 1
                  }
                  background={
                    selectedAlert?.id === alert?.id
                      ? colors.primary.main // Primary color for selected alert
                      : compareAlertId === alert?.id.toString()
                        ? "" // Black for comparison alert
                        : "" // Default pink color
                  }
                  borderColor="transparent"
                  glyphColor={glyphColor}
                  glyph={
                    selectedAlert?.id === alert?.id ||
                    compareAlertId === alert?.id.toString()
                      ? (addressIndex + 1).toString()
                      : undefined
                  }
                />
              </AdvancedMarker>
            );
          }
          return null;
        }) || [],
    );

  return (
    <div style={containerStyle}>
      <Map
        colorScheme={mapColorScheme}
        onTilesLoaded={() => {
          setIsMapLoaded(true);
          setBounds(map?.getBounds());
        }}
        mapId={googleMapId}
        onBoundsChanged={() => {
          setBounds(map?.getBounds());
        }}
        defaultZoom={zoomFactor}
        defaultCenter={{ lat: 37.0902, lng: -95.7129 }}
        minZoom={3}
        fullscreenControl={false}
      >
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {isMapLoaded && getMarkers(alerts)}

        {isMapLoaded && (
          <MapControl position={google.maps.ControlPosition.RIGHT_BOTTOM}>
            <Box
              sx={{
                bgcolor: "background.paper",
                boxShadow: 2,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                margin: "10px",
              }}
            >
              <IconButton
                onClick={panToCurrentLocation}
                aria-label="Pan to current location"
              >
                <MyLocationIcon />
              </IconButton>
            </Box>
          </MapControl>
        )}
        {isMapLoaded && usersLocation?.lat && usersLocation?.lng && (
          <Marker
            position={usersLocation}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: colors.primary.main,
              fillOpacity: 1,
              strokeColor: colors.primary.contrastText,
              strokeWeight: 2,
            }}
            onClick={panToCurrentLocation}
            zIndex={1000}
          />
        )}
      </Map>
    </div>
  );
}

export default AlertsMap;
